/** @format */

import { Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  GA4_COMPONENT_NAME,
  GA4_COMPONENT_POSITION,
  GA4_COMPONENT_TYPE
} from 'src/analytics/componentInteractionGa4';
import { Button } from 'src/components';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import useGa4Analytics from 'src/hooks/useGa4Analytics';
import useQuery from 'src/hooks/useQuery';
import { useDispatch, useSelector } from 'src/store';
import { getNewsArticles } from 'src/slices/news';
import { startViewTransition } from 'src/utils/viewTransition';
import { NewsItem, NewsListSkeleton } from 'src/components/news/item';

export default function NewsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery().get('query');
  const { t, i18n } = useTranslation();
  const { trackComponentInteraction } = useGa4Analytics();
  const { error, newsArticles, newsArticlesLoading } = useSelector(state => state.news);
  const [displayedState, setDisplayedState] = useState(() => ({
    loading: Boolean(newsArticlesLoading),
    articles: newsArticles,
    error
  }));
  const previousLoadingRef = useRef(newsArticlesLoading);
  useEffect(() => {
    dispatch(
      getNewsArticles({
        query: query || ''
      })
    );
  }, [dispatch, query]);

  useEffect(() => {
    const wasLoading = previousLoadingRef.current;
    previousLoadingRef.current = newsArticlesLoading;

    if (wasLoading && !newsArticlesLoading) {
      startViewTransition(() => {
        setDisplayedState({ loading: false, articles: newsArticles, error });
      });
    } else if (newsArticlesLoading && !wasLoading) {
      setDisplayedState(prevState => ({
        loading: true,
        articles:
          Array.isArray(prevState.articles) && prevState.articles.length > 0 ? prevState.articles : newsArticles,
        error: null
      }));
    } else if (!newsArticlesLoading) {
      setDisplayedState({ loading: false, articles: newsArticles, error });
    }
  }, [newsArticlesLoading, newsArticles, error]);

  const pageTitle = t('news.title');
  const displayedArticles = Array.isArray(displayedState.articles) ? displayedState.articles : [];
  const hasDisplayedArticles = displayedArticles.length > 0;
  const shouldShowSkeleton = displayedState.loading && !hasDisplayedArticles;
  const shouldDimArticles = displayedState.loading && hasDisplayedArticles;

  const handleBackToHomeClick = useCallback(() => {
    trackComponentInteraction({
      component_name: GA4_COMPONENT_NAME.BACK_TO_HOME,
      component_type: GA4_COMPONENT_TYPE.BUTTON,
      component_position: GA4_COMPONENT_POSITION.NEWS_LIST
    });
    navigate('/dashboard');
  }, [navigate, trackComponentInteraction]);

  return (
    <Page className={`archive-page news-page`} title={pageTitle}>
      <Breadcrumb data={[{ title: pageTitle }]} additionalClasses="container--narrow" />
      <Grid className="archive-page-container container container--narrow">
        <Grid className="archive-page-title-box news-page-title-box">
          <h1 className="archive-page-title news-page-title">{pageTitle}</h1>
        </Grid>

        <Grid
          className={`news-list archive-page-list${shouldDimArticles ? ' news-list--loading' : ''}`}
          style={{ viewTransitionName: 'news-list' }}
          aria-busy={shouldDimArticles}>
          {displayedState.error ? (
            <p>{t('errorpage.text')}</p>
          ) : shouldShowSkeleton ? (
            <NewsListSkeleton count={5} />
          ) : displayedArticles.length > 0 ? (
            displayedArticles.map(item => <NewsItem item={item} key={item.nid} />)
          ) : (
            <p className="news-list-empty">{t('common.noResults')}</p>
          )}
        </Grid>
        <Button onClick={handleBackToHomeClick} borderOnly back label={t('common.button.backToHome')} />
      </Grid>
    </Page>
  );
}
